'use client';

import React from 'react';
import Link from 'next/link';
import { useSite } from '@/context/SiteContext';
import {
  FileText, Package, AlertTriangle, UserCheck, User, Ban,
  Lock, Database, ShieldAlert, ShieldX, Scale, HandCoins,
  LogOut, Gavel, RefreshCw, Mail, ChevronRight,
} from 'lucide-react';

const SECTIONS = [
  { id: 'accepting',      num: 1,  icon: FileText,    title: 'Accepting These Terms' },
  { id: 'offer',          num: 2,  icon: Package,     title: 'What We Offer' },
  { id: 'info-only',      num: 3,  icon: AlertTriangle,title: 'Information Only — Not Advice' },
  { id: 'eligibility',   num: 4,  icon: UserCheck,   title: 'Who Can Use This Site' },
  { id: 'account',       num: 5,  icon: User,        title: 'Your Account' },
  { id: 'usage',         num: 6,  icon: Ban,         title: 'How You May Use the Site' },
  { id: 'ip',            num: 7,  icon: Lock,        title: 'Our Intellectual Property' },
  { id: 'third-party',   num: 8,  icon: Database,    title: 'Third-Party Data' },
  { id: 'accuracy',      num: 9,  icon: ShieldAlert, title: 'Data Accuracy' },
  { id: 'warranties',    num: 10, icon: ShieldX,     title: 'No Warranties' },
  { id: 'liability',     num: 11, icon: Scale,       title: 'Limits on Our Liability' },
  { id: 'indemnity',     num: 12, icon: HandCoins,   title: 'Your Responsibility to Us' },
  { id: 'termination',   num: 13, icon: LogOut,      title: 'Ending Your Access' },
  { id: 'governing-law', num: 14, icon: Gavel,       title: 'Governing Law and Disputes' },
  { id: 'updates',       num: 15, icon: RefreshCw,   title: 'Updates to These Terms' },
  { id: 'contact',       num: 16, icon: Mail,        title: 'Get in Touch' },
];

function SectionHeading({ id, num, icon: Icon, title }: { id: string; num: number; icon: React.ElementType; title: string }) {
  return (
    <div id={id} className="flex items-center gap-3 mb-4 scroll-mt-24">
      <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
        <Icon size={15} className="text-sky-400" />
      </div>
      <h2 className="text-base font-semibold text-white">
        <span className="text-sky-500 mr-2 font-mono text-sm">{num}.</span>{title}
      </h2>
    </div>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-fg-muted leading-relaxed space-y-3">{children}</div>;
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 mt-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm text-fg-muted">
          <ChevronRight size={13} className="text-sky-500 flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function LegalBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-default rounded-xl px-4 py-3 text-xs text-fg-subtle leading-relaxed uppercase tracking-wide">
      {children}
    </div>
  );
}

export default function TermsPage() {
  const { siteName } = useSite();
  return (
    <div className="min-h-screen bg-surface">

      {/* Hero */}
      <div className="border-b border-default bg-surface">
        <div className="max-w-5xl mx-auto px-2 py-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
              <FileText size={20} className="text-sky-400" />
            </div>
            <span className="text-xs font-semibold tracking-widest text-fg-subtle uppercase">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">Terms of Service</h1>
          <div className="flex flex-wrap gap-4 text-xs text-fg-subtle mb-5">
            <span><span className="text-fg-muted font-medium">Effective date:</span> April 1, 2026</span>
            <span><span className="text-fg-muted font-medium">Last updated:</span> April 1, 2026</span>
          </div>
          {/* Important callout */}
          <div className="flex items-start gap-3 bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3 max-w-2xl">
            <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300/80 leading-relaxed">
              <span className="font-semibold text-amber-300">Important:</span> Everything on {siteName} is for informational purposes only. Nothing here is financial, investment, legal, or tax advice. Please talk to a qualified professional before making any investment decisions.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-2 py-10 flex gap-10">

        {/* Sticky table of contents */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-6 bg-card-bg border border-default rounded-xl p-4 max-h-[calc(100vh-3rem)] overflow-y-auto">
            <p className="text-xs font-semibold text-fg-subtle uppercase tracking-widest mb-3">Contents</p>
            <nav className="space-y-0.5">
              {SECTIONS.map(({ id, num, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-fg-muted hover:text-white hover:bg-card-hover-bg transition-colors"
                >
                  <span className="text-sky-500 font-mono w-4 text-right flex-shrink-0">{num}</span>
                  {title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* 1 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="accepting" num={1} icon={FileText} title="Accepting These Terms" />
            <Prose>
              <p>By visiting or using stocksiqpro.com (&ldquo;the Site&rdquo;), you agree to these Terms of Service (&ldquo;Terms&rdquo;) and our <Link href="/privacy" className="text-sky-400 hover:text-sky-300 underline underline-offset-2">Privacy Policy</Link>. If you don&apos;t agree, please don&apos;t use the Site.</p>
              <p>These Terms form a binding agreement between you and {siteName}. If we update the Terms and you keep using the Site, that counts as acceptance of the new version.</p>
            </Prose>
          </section>

          {/* 2 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="offer" num={2} icon={Package} title="What We Offer" />
            <Prose>
              <p>{siteName} is a financial data and research platform. We provide:</p>
              <BulletList items={[
                'Stock and ETF prices (real-time, delayed, and end-of-day)',
                'Financial statements: income statements, balance sheets, and cash flow data',
                'Historical records and corporate actions',
                'Analyst ratings, price targets, and earnings forecasts',
                'ETF holdings, dividend data, and other market information',
                'Coverage of over 100,000 tradable symbols across major global exchanges',
              ]} />
              <p className="text-xs text-fg-subtle border-l-2 border-default pl-3">We may change, pause, or remove any part of the Site at any time, with or without advance notice.</p>
            </Prose>
          </section>

          {/* 3 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="info-only" num={3} icon={AlertTriangle} title="Information Only — Not Advice" />
            <Prose>
              <p>All data, analysis, prices, and content on the Site are strictly for informational and educational use. Nothing here should be taken as:</p>
              <BulletList items={[
                'A recommendation to buy, sell, or hold any security',
                'Financial, investment, tax, or legal advice',
                'An offer or solicitation to trade any security',
                'A promise of returns or future performance',
              ]} />
              <p className="text-xs text-amber-300/70 border-l-2 border-amber-500/30 pl-3">Past performance is not a reliable indicator of future results. You are solely responsible for your investment decisions. We strongly encourage you to speak with a licensed financial advisor before investing.</p>
            </Prose>
          </section>

          {/* 4 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="eligibility" num={4} icon={UserCheck} title="Who Can Use This Site" />
            <Prose>
              <p>You must be at least 13 years old to use the Site. If you&apos;re under 18, you confirm that a parent or guardian has given their consent. By using the Site, you also confirm that no applicable law in your jurisdiction prevents you from doing so.</p>
            </Prose>
          </section>

          {/* 5 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="account" num={5} icon={User} title="Your Account" />
            <Prose>
              <p>Some features require you to create an account. If you do, you agree to:</p>
              <BulletList items={[
                'Provide accurate and up-to-date information when signing up',
                'Keep your account details current',
                'Keep your password private and secure',
                'Let us know immediately if someone accesses your account without permission',
                'Take responsibility for everything that happens under your account',
              ]} />
              <p className="text-xs text-fg-subtle border-l-2 border-default pl-3">We may suspend or delete accounts that break these Terms or have been inactive for a long time.</p>
            </Prose>
          </section>

          {/* 6 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="usage" num={6} icon={Ban} title="How You May (and May Not) Use the Site" />
            <Prose>
              <p>You agree to use the Site lawfully and in line with these Terms. The following are not allowed:</p>
              <BulletList items={[
                'Scraping or bulk-extracting data without our written permission',
                'Using bots, scripts, or automated tools in a way that strains our systems',
                'Reselling or redistributing Site data for commercial gain',
                'Trying to access parts of the Site, servers, or systems you\'re not authorized to use',
                'Reverse-engineering or decompiling any underlying software or data',
                'Uploading malware, viruses, or anything that disrupts Site operations',
                'Using the Site for fraud, market manipulation, or any illegal activity',
                `Impersonating another person or falsely claiming affiliation with ${siteName}`,
              ]} />
              <p className="text-xs text-red-400/70 border-l-2 border-red-500/30 pl-3">Breaking these rules can result in immediate loss of access and, where appropriate, legal action.</p>
            </Prose>
          </section>

          {/* 7 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="ip" num={7} icon={Lock} title="Our Intellectual Property" />
            <Prose>
              <p>Everything on the Site — text, graphics, logos, design, software, and original data compilations — belongs to or is licensed by {siteName} and is protected by copyright, trademark, and other intellectual property laws.</p>
              <p>We grant you a limited, personal, non-commercial license to access and use the Site. You may not copy, distribute, modify, or commercially exploit any content without our explicit written consent.</p>
              <p>Financial data displayed on the Site comes from third-party providers and remains subject to their intellectual property rights. Downloading or redistributing that data without authorization is not permitted.</p>
              <p>Company logos and trademarks displayed on the Site are the property of their respective owners. {siteName} is not affiliated with, endorsed by, or sponsored by any of these companies. Logos are used for identification purposes only under nominative fair use.</p>
            </Prose>
          </section>

          {/* 8 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="third-party" num={8} icon={Database} title="Third-Party Data" />
            <Prose>
              <p>We source data from providers including S&amp;P Global Market Intelligence, CBOE, Benzinga, Finnhub, Nasdaq Data Link, and Financial Modeling Prep. Accessing their data through our Site does not give you any rights beyond personal viewing — each provider has its own terms that apply.</p>
              <p>The Site may also link to external websites. We don&apos;t endorse or control those sites, and visiting them is at your own risk.</p>
            </Prose>
          </section>

          {/* 9 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="accuracy" num={9} icon={ShieldAlert} title="Data Accuracy" />
            <Prose>
              <p>We work hard to keep our data accurate — through manual reviews, regular scans, and close coordination with our providers. That said, we can&apos;t guarantee that everything is always complete, current, or error-free.</p>
              <p>Data can sometimes be affected by provider delays, corporate events like mergers or ticker changes, or discrepancies between sources. If you spot something wrong, please email us at <a href="mailto:support@stocksiqpro.com" className="text-sky-400 hover:text-sky-300 underline underline-offset-2">support@stocksiqpro.com</a> and we&apos;ll look into it.</p>
              <p className="text-xs text-amber-300/70 border-l-2 border-amber-500/30 pl-3">Using any data from this Site for trading or investment purposes is entirely at your own risk.</p>
            </Prose>
          </section>

          {/* 10 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="warranties" num={10} icon={ShieldX} title="No Warranties" />
            <LegalBlock>
              The site and all of its content are provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; with no warranties of any kind — express or implied. To the fullest extent allowed by law, we disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the site will be uninterrupted, secure, or free of errors or harmful code.
            </LegalBlock>
          </section>

          {/* 11 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="liability" num={11} icon={Scale} title="Limits on Our Liability" />
            <LegalBlock>
              To the maximum extent permitted by law, {siteName} — including its officers, directors, employees, agents, and data providers — will not be liable for any indirect, incidental, special, consequential, or punitive damages. This includes lost profits, trading losses, or data loss, whether or not we were warned that such damages were possible.
            </LegalBlock>
            <p className="text-xs text-fg-subtle mt-3 leading-relaxed">Where local law doesn&apos;t allow certain exclusions, our liability is limited to the fullest extent the law permits. In all cases, our total liability to you will not exceed the greater of $100 USD or the amount you paid us in the twelve months before your claim.</p>
          </section>

          {/* 12 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="indemnity" num={12} icon={HandCoins} title="Your Responsibility to Us" />
            <Prose>
              <p>You agree to defend and hold harmless {siteName}, its affiliates, officers, directors, employees, and data providers from any claims, damages, losses, or expenses — including legal fees — that arise from: (a) your use of the Site; (b) your violation of these Terms; (c) your infringement of any third-party rights; or (d) any investment decisions you make based on content from the Site.</p>
            </Prose>
          </section>

          {/* 13 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="termination" num={13} icon={LogOut} title="Ending Your Access" />
            <Prose>
              <p>We may suspend or terminate your access to the Site at any time, for any reason — including violations of these Terms, suspected abuse or fraud, or extended inactivity.</p>
              <p>When access ends, all licenses granted to you immediately expire. Sections that are meant to survive termination — such as disclaimers, liability limits, and indemnification — will continue to apply.</p>
            </Prose>
          </section>

          {/* 14 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="governing-law" num={14} icon={Gavel} title="Governing Law and Disputes" />
            <Prose>
              <p>These Terms are governed by the laws of the United States and the state where {siteName} is incorporated, without regard to conflict-of-law rules.</p>
              <p>If a dispute arises, we ask that you contact us first and give us 30 days to resolve it informally. If that doesn&apos;t work, the dispute will go to binding individual arbitration under the rules of the American Arbitration Association. You give up the right to join any class action lawsuit or class-wide arbitration.</p>
              <p className="text-xs text-fg-subtle border-l-2 border-default pl-3">If you&apos;re in a jurisdiction where arbitration clauses aren&apos;t enforceable, you retain the right to pursue your claim in local courts.</p>
            </Prose>
          </section>

          {/* 15 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="updates" num={15} icon={RefreshCw} title="Updates to These Terms" />
            <Prose>
              <p>We may update these Terms from time to time. When we make significant changes, we&apos;ll update the &ldquo;Last updated&rdquo; date at the top of this page. Continuing to use the Site after changes are posted means you accept the revised Terms. If you don&apos;t agree with an update, stop using the Site.</p>
            </Prose>
          </section>

          {/* 16 */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="contact" num={16} icon={Mail} title="Get in Touch" />
            <Prose>
              <p>Questions or concerns about these Terms? Reach us at:</p>
            </Prose>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:support@stocksiqpro.com"
                className="flex items-center gap-2 px-4 py-3 bg-surface border border-default hover:border-sky-500/40 rounded-xl text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Mail size={15} className="text-sky-400" />
                support@stocksiqpro.com
              </a>
              <Link
                href="/contact"
                className="flex items-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 rounded-xl text-sm text-white font-medium transition-colors"
              >
                Contact Form <ChevronRight size={14} />
              </Link>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
